const formatterCache = new Map();
function formatter(timeZone) {
    let f = formatterCache.get(timeZone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-GB', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatterCache.set(timeZone, f);
    }
    return f;
}
/** Wall-clock parts of an instant in a time zone. */
export function zonedParts(epochMs, timeZone) {
    const parts = formatter(timeZone).formatToParts(new Date(epochMs));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
        second: get('second'),
    };
}
/** Offset of the zone from UTC at an instant, in milliseconds (positive east of UTC). */
function offsetAt(epochMs, timeZone) {
    const p = zonedParts(epochMs, timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - Math.floor(epochMs / 1000) * 1000;
}
/**
 * Converts a local date + wall-clock time in `timeZone` to an epoch instant.
 * - Times that don't exist (the skipped hour when clocks go forward) move forward by the gap.
 * - Times that exist twice (when clocks go back) resolve to the first occurrence.
 */
export function localToEpoch(date, time, timeZone) {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);
    const naive = Date.UTC(y, m - 1, d, hh, mm);
    // Try the offsets valid just before and just after the naive instant; pick the
    // earliest candidate that maps back to the same wall-clock time.
    const offsets = [offsetAt(naive - 12 * 3_600_000, timeZone), offsetAt(naive + 12 * 3_600_000, timeZone)];
    const candidates = [...new Set(offsets)]
        .map((o) => naive - o)
        .filter((t) => {
        const p = zonedParts(t, timeZone);
        return p.hour === hh && p.minute === mm && p.day === d;
    })
        .sort((a, b) => a - b);
    if (candidates.length > 0)
        return candidates[0];
    // Non-existent local time: apply the offset from before the change, which lands just after the gap.
    return naive - Math.min(...offsets);
}
/** Local calendar date of an instant in a time zone. */
export function localDateOf(epochMs, timeZone) {
    const p = zonedParts(epochMs, timeZone);
    return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
/** ISO weekday (1 = Monday) of a local date. */
export function weekdayOf(date) {
    const [y, m, d] = date.split('-').map(Number);
    const js = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
    return (js === 0 ? 7 : js);
}
/** Every local date from `from` to `to`, inclusive. */
export function eachDate(from, to) {
    const out = [];
    const [y, m, d] = from.split('-').map(Number);
    const cursor = new Date(Date.UTC(y, m - 1, d));
    for (let guard = 0; guard < 800; guard++) {
        const iso = cursor.toISOString().slice(0, 10);
        if (iso > to)
            break;
        out.push(iso);
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return out;
}
/** Turns weekly wall-clock hours into concrete UTC intervals for a range of local dates. */
export function expandWeeklyHours(hours, from, to, timeZone) {
    const out = [];
    for (const date of eachDate(from, to)) {
        for (const range of hours[weekdayOf(date)] ?? []) {
            const start = localToEpoch(date, range.start, timeZone);
            const end = range.end === '24:00' ? localToEpoch(nextDate(date), '00:00', timeZone) : localToEpoch(date, range.end, timeZone);
            if (end > start)
                out.push({ start, end });
        }
    }
    return out;
}
/** Whole local days (from 00:00 to the next 00:00) as UTC intervals. */
export function localDaysToIntervals(dates, timeZone) {
    return dates.map((date) => ({
        start: localToEpoch(date, '00:00', timeZone),
        end: localToEpoch(nextDate(date), '00:00', timeZone),
    }));
}
export function nextDate(date) {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}
//# sourceMappingURL=time.js.map